import React from 'react';
import dayjs from 'dayjs';

// Reusable MetricCard component
const MetricCardComponent = ({
  className,
  value,
  label,
  color,
  trendDisplay,
  isFetching,
}: {
  className: string;
  value: number;
  label: string;
  color: string;
  trendDisplay: any;
  isFetching: boolean;
}) => {
  const getBorderColor = () => {
    if (className === 'critical') return 'before:bg-red-600';
    if (className === 'high') return 'before:bg-amber-500';
    if (className === 'medium') return 'before:bg-blue-500';
    if (className === 'low') return 'before:bg-emerald-500';
    return '';
  };

  const getTrendClasses = (trendClass: string) => {
    if (trendClass === 'trend-up') {
      return 'inline-flex items-center gap-1 text-xs mt-2 px-2 py-1 rounded-xl bg-red-100 text-red-600';
    }
    if (trendClass === 'trend-down') {
      return 'inline-flex items-center gap-1 text-xs mt-2 px-2 py-1 rounded-xl bg-emerald-100 text-emerald-600';
    }
    return 'inline-flex items-center gap-1 text-xs mt-2 px-2 py-1 rounded-xl bg-blue-100 text-blue-500';
  };

  return (
    <div
      className={`bg-[#fafafa] border border-slate-200 rounded-xl pl-6 pr-4 py-4 transition-all cursor-pointer relative overflow-hidden h-fit gap-2 hover:-translate-y-0.5 hover:shadow-lg ${getBorderColor()} before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1`}
    >
      <div
        style={{ color }}
        className='text-[34px] font-bold my-2 leading-none'
      >
        {isFetching ? '...' : value.toLocaleString()}
      </div>
      <p className='text-sm text-slate-500'>{label}</p>
      {trendDisplay && !isFetching ? (
        <div className={getTrendClasses(trendDisplay.className)}>
          <span>{trendDisplay.arrow}</span>
          <span>{trendDisplay.text}</span>
        </div>
      ) : isFetching ? (
        <div className='inline-flex items-center gap-1 text-xs mt-2 px-2 py-1 rounded-xl bg-emerald-100 text-emerald-600'>
          <span>...</span>
          <span>Loading trend data...</span>
        </div>
      ) : null}
    </div>
  );
};

interface CollisionRiskDashboardProps {
  urgentRiskCount: number;
  highRiskCount: number;
  totalMonitoredCount: number;
  urgentRiskTrendDisplay: any;
  highRiskTrendDisplay: any;
  totalMonitoredTrendDisplay: any;
  isFetchingMetrics: boolean;
  dateRange?: {
    start: string;
    end: string;
  };
}

const CollisionRiskDashboard: React.FC<CollisionRiskDashboardProps> = ({
  urgentRiskCount,
  highRiskCount,
  totalMonitoredCount,
  urgentRiskTrendDisplay,
  highRiskTrendDisplay,
  totalMonitoredTrendDisplay,
  isFetchingMetrics,
  dateRange,
}) => {
  return (
    <div className='bg-white rounded-2xl p-4 w-full mt-6 max-w-[1400px] shadow-lg'>
      <div className='flex flex-col gap-2 items-start'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br from-red-100 to-red-200'>
            ⚠️
          </div>
          <div className='flex-1'>
            <p className='text-lg font-bold text-slate-800'>
              Conjunction Assessment Summary
            </p>
          </div>
        </div>
        {dateRange?.start && dateRange?.end && (
          <div className='text-sm text-gray-600 mb-4'>
            {dayjs.utc(dateRange.start).format('YYYY-MM-DD HH:mm') + ' ~ '}
            {dayjs.utc(dateRange.end).format('YYYY-MM-DD HH:mm') +
              ', 3 days'}
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 gap-3'>
        <MetricCardComponent
          className='critical'
          value={urgentRiskCount}
          label='High Risk (DCA < 1km)'
          color='#dc2626'
          trendDisplay={urgentRiskTrendDisplay}
          isFetching={isFetchingMetrics}
        />

        <MetricCardComponent
          className='high'
          value={highRiskCount}
          label='Medium Risk (DCA < 5km)'
          color='#f59e0b'
          trendDisplay={highRiskTrendDisplay}
          isFetching={isFetchingMetrics}
        />

        <MetricCardComponent
          className='low'
          value={totalMonitoredCount}
          label='Total (DCA < 10km)'
          color='#10b981'
          trendDisplay={totalMonitoredTrendDisplay}
          isFetching={isFetchingMetrics}
        />
      </div>
    </div>
  );
};

export default CollisionRiskDashboard;
